import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './style.css';
import { useGet } from '@/api/hooks';
import { CreatePostService, GetMedicalRequestDetails } from '@/api/endpoints/Requests';
import { errorToast, successToast } from '@/utils/toasters';

const PostServiceEntry = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { t } = useTranslation();

    const { result: requestDetails, isPending } = useGet({
        endPoint: GetMedicalRequestDetails,
        depArr: [],
        allowFetch: !!state?.requestId,
        params: [3]
    });

    const [loading, setLoading] = useState(false);

    const [serviceRecords, setServiceRecords] = useState([
        {
            id: 1,
            name: '',
            price: '',
            reason: ''
        }
    ]);

    const handleAddService = () => {
        setServiceRecords([
            ...serviceRecords,
            {
                id: serviceRecords.length + 1,
                name: '',
                price: '',
                reason: ''
            }
        ]);
    };

    const handleRemoveService = (id) => {
        if (serviceRecords.length > 1) {
            setServiceRecords(serviceRecords.filter(record => record.id !== id));
        }
    };

    const handleServiceChange = (id, field, value) => {
        setServiceRecords(serviceRecords.map(record =>
            record.id === id ? { ...record, [field]: value } : record
        ));
    };

    const calculateTotal = () => {
        return serviceRecords.reduce((sum, record) => {
            return sum + (parseFloat(record.price) || 0);
        }, 0).toFixed(2);
    };

    const handleCancel = () => {
        navigate("/panel/provider/requests/medical")
    };


    const handleSubmit = () => {
        if(serviceRecords.length === 0 || serviceRecords.some(record => record.name === '' || record.price === '' || record.reason === '')) {
            errorToast("error-validation");
            return;
        }
        const data = {
            hospitalOrderId: state?.requestId,
            services: serviceRecords
        }
        setLoading(true);
        CreatePostService(data).
            then(response => {
                successToast("Operation Done Successfully");
                setLoading(false);
                navigate("/panel/provider/requests/medical")
            }).catch(error => {
                setLoading(false);
                console.log('Error', error);
            });
        // Add submission logic
    };

    return (
        <div className="post-service-entry-container">
            {/* Header */}
            <div className="post-service-entry-header">
                <h1 className="post-service-entry-title">{t('post-service-entry')}</h1>
                <p className="post-service-entry-subtitle">
                    {t('record-finalize-clinical-details-one-more-procedures')}
                </p>
            </div>

            {/* Patient Identity */}
            <div className="post-service-patient-identity">
                <div className="post-service-patient-badge">{t('patient-identity')}</div>
                <h2 className="post-service-patient-name">{requestDetails?.patientData?.name}</h2>
                <div className="post-service-patient-meta">
                    <span className="post-service-patient-meta-item">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                            <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                            <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                            <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
                        </svg>
                        {t('age')}: {requestDetails?.patientData?.age}
                    </span>
                    <span className="post-service-patient-meta-item">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        {t('patient-id')}: {requestDetails?.patientData?.id}
                    </span>
                </div>
            </div>

            {/* Service Categories */}
            <div className="post-service-categories">
                <div className="post-service-category-card">
                    <div className="post-service-category-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div className="post-service-category-label">{t('category')}</div>
                    <div className="post-service-category-value">{requestDetails?.requestData?.category}</div>
                </div>
                <div className="post-service-category-card">
                    <div className="post-service-category-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </div>
                    <div className="post-service-category-label">{t('sub-category')}</div>
                    <div className="post-service-category-value">{requestDetails?.requestData?.subCategory}</div>
                </div>
            </div>

            {/* Service Records */}
            {serviceRecords.map((record, index) => (
                <div key={record.id} className="post-service-record">
                    <div className="post-service-record-header">
                        <h3 className="post-service-record-title">
                            {t('service-record')} #{record.id}
                        </h3>
                        {serviceRecords.length > 1 && (
                            <button
                                className="post-service-record-delete"
                                onClick={() => handleRemoveService(record.id)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        )}
                    </div>
                    <div className="post-service-record-subtitle">{t('enter-precise-details')}</div>

                    <div className="post-service-record-row">
                        <div className="post-service-record-field">
                            <label className="post-service-record-label">
                                {t('specific-service-performed')}
                            </label>
                            <input
                                type="text"
                                className="post-service-record-input"
                                placeholder={t('eg-transthoracic-echocardiogram-tte')}
                                value={record.name}
                                onChange={(e) => handleServiceChange(record.id, 'name', e.target.value)}
                            />
                        </div>
                        <div className="post-service-record-field post-service-record-field-cost">
                            <label className="post-service-record-label">
                                {t('service-cost')} ($)
                            </label>
                            <div className="post-service-record-input-group">
                                <span className="post-service-record-currency">$</span>
                                <input
                                    type="number"
                                    className="post-service-record-input post-service-record-input-cost"
                                    placeholder="0.00"
                                    value={record.price}
                                    onChange={(e) => handleServiceChange(record.id, 'price', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="post-service-record-field">
                        <label className="post-service-record-label">
                            {t('reason-service-clinical-justification')}
                        </label>
                        <textarea
                            className="post-service-record-textarea"
                            rows="4"
                            placeholder={t('enter-clinical-notes-patient-symptoms-justification')}
                            value={record.reason}
                            onChange={(e) => handleServiceChange(record.id, 'reason', e.target.value)}
                        />
                    </div>
                </div>
            ))}

            {/* Add Another Service Button */}
            <button className="post-service-add-btn" onClick={handleAddService}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                    <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                {t('add-another-service')}
            </button>

            {/* Footer */}
            <div className="post-service-footer">
                <div className="post-service-total">
                    <div className="post-service-total-label">{t('estimated-total')}</div>
                    <div className="post-service-total-value">${calculateTotal()}</div>
                </div>
                <div className="post-service-actions">
                    <button disabled={loading} className="post-service-cancel-btn" onClick={handleCancel}>
                        {t('cancel-entry')}
                    </button>
                    <button disabled={loading} className="post-service-submit-btn" onClick={handleSubmit}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      {loading ? t('loading') : t('submit-post-service')}
                    </button>
                </div>
            </div>

            {/* Footer Note */}
            <div className="post-service-footer-note">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t('secure-health-information-system-hipaa-compliant-session')}
            </div>
        </div>
    );
};

export default PostServiceEntry;

